import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/apiClient'
import type {
  Depot,
  DepotDetail,
  DepotMovesResponse,
  DepotSelect,
} from '@/types/backend'

async function getDepotMoves(id?: string) {
  if (!id) return null
  const { data } = await apiClient.get<DepotMovesResponse>(
    `/depots/${id}/moves`
  )
  return data
}

export const depotQK = {
  list: () => ['depots'] as const,
  select: () => ['depots', '_select'] as const,
  one: (id: string) => ['depots', id] as const,
  moves: (id: string) => ['depots', id, 'moves'] as const,
}

export const useDepotsQuery = (enabled = true) =>
  useQuery({
    queryKey: depotQK.list(),
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<Depot[]>('/depots')
      return data
    },
  })

export const useDepotSelectQuery = (enabled = true) =>
  useQuery({
    queryKey: depotQK.select(),
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<DepotSelect[]>('/depots/_select')
      return data
    },
    staleTime: 5 * 60 * 1000,
  })

export const useDepotQuery = (id: string, enabled = true) =>
  useQuery({
    queryKey: depotQK.one(id),
    enabled: Boolean(id) && enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<DepotDetail>(`/depots/${id}`)
      return data
    },
  })

export const useCreateDepotMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: Partial<Depot>) => {
      const { data } = await apiClient.post<Depot>('/depots', payload)
      return data
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['depots'] })
      qc.setQueryData(depotQK.one(created.id), created)
      qc.invalidateQueries({ queryKey: depotQK.select() })
    },
  })
}

export const useUpdateDepotMutation = (id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: Partial<Depot>) => {
      const { data } = await apiClient.patch<Depot>(`/depots/${id}`, payload)
      return data
    },
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['depots'] })
      qc.setQueryData(depotQK.one(id), updated)
      qc.invalidateQueries({ queryKey: depotQK.select() })
    },
  })
}

export const useDeleteDepotMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.delete<Depot>(`/depots/${id}`)
      return data
    },
    onSuccess: (_deleted, id) => {
      qc.invalidateQueries({ queryKey: ['depots'] })
      qc.removeQueries({ queryKey: depotQK.one(id) })
      qc.invalidateQueries({ queryKey: depotQK.select() })
    },
  })
}

export const useDepotMovesQuery = (id: string | undefined, enabled = true) =>
  useQuery({
    queryKey: id ? depotQK.moves(id) : ['depots', 'moves', 'none'],
    enabled: Boolean(id) && enabled,
    queryFn: () => getDepotMoves(id),
  })
